import { Box, Stack, Typography } from "@mui/material";
import useApiServices from "src/api/use-api-services";
import AppButton from "src/components/common/app-button";
import { filtersDefaultValues } from "src/constants/filters-default-values";
import { PLATFORMS } from "src/constants/platforms";
import { useBoolean } from "src/hooks/use-boolean";
import { CsvDto } from "src/types/csv.dto";
import { downloadCsvFile } from "src/utils/api/download-csv";
import { useQueryParams } from "use-query-params";
import FilterDrawer from "./filter-drawer";
import Iconify from "src/components/iconify";
import { AxiosError } from "axios";
import ChosenFiltersView from "./chosen-filters-view";

type DashboardHeaderType = {
  platform: keyof typeof PLATFORMS;
  type: "main" | "custom";
  error: AxiosError | null;
  isActivityCompleted: boolean;
};

const PlatformHeader = ({ platform, type, error, isActivityCompleted }: DashboardHeaderType) => {
  // #region Hooks and states
  const { useGetListApi } = useApiServices();

  const downloadCsv = useBoolean();
  const openFiltersDrawer = useBoolean();
  const [query] = useQueryParams(filtersDefaultValues);
  // #endregion

  // #region Services
  const { isFetching: isDownloading } = useGetListApi<CsvDto>({
    url: PLATFORMS[platform][type].CSV,
    onSuccess: downloadCsvFile,
    enabled: downloadCsv.value,
    parameters: {
      end_date: query.end_date || undefined,
      start_date: query.start_date || undefined,
      keyword: query.keyword,
      language: query.language,
      sentiment: query.sentiment,
      author_verified: query.author_verified,
    },
  });
  // #endregion

  return (
    <Stack spacing={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        marginBottom={2}
        flexDirection={{ xs: "column", md: "row" }}
        textAlign={{ xs: "center", md: "left" }}
        gap={{ xs: "10px", md: "2px" }}
      >
        <Typography variant="h2">
          {PLATFORMS[platform].label[0].toUpperCase() + PLATFORMS[platform].label.slice(1)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterDrawer
            isActivityCompleted={isActivityCompleted}
            open={openFiltersDrawer}
            type={type}
            platform={platform}
          />
          <AppButton
            label="Export Report"
            variant="contained"
            size="large"
            onClick={downloadCsv.onTrue}
            disabled={!isActivityCompleted}
            isLoading={isDownloading}
            fullWidth={false}
            startIcon={
              <Iconify icon="material-symbols-light:download-sharp" width={24} height={24} />
            }
          />
        </Stack>
      </Box>

      <ChosenFiltersView onEdit={openFiltersDrawer.onTrue} />
    </Stack>
  );
};

export default PlatformHeader;
